import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Chips, tabs and segmented controls, from section 06 of the kit.
 *
 * A filter chip turns ink when it is carrying a value, and shows the count of
 * what it is filtering by. That is the only way to tell at a glance whether a
 * list is showing everything.
 */

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Ink background: this filter is doing something. */
  active?: boolean;
  /** How many values are selected. Shown only when active. */
  count?: number;
  icon?: ReactNode;
  trailing?: ReactNode;
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { active, count, icon, trailing, className, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-pressed={active}
      className={cn(
        'inline-flex h-[34px] items-center gap-[7px] rounded-pill px-[14px] text-chip',
        'transition-colors duration-base ease-emphasis',
        active
          ? 'bg-ink font-semibold text-on-ink hover:bg-ink-soft'
          : 'border border-line-strong font-medium text-ink hover:bg-canvas-tint',
        className,
      )}
      {...props}
    >
      {icon}
      {children}
      {active && count !== undefined && count > 0 && (
        <span className="text-meta-sm font-semibold text-on-ink-muted">{count}</span>
      )}
      {trailing}
    </button>
  );
});

/** A chip stating a value rather than opening a menu, e.g. "Due this week". */
export function ValueChip({
  children,
  onClear,
  clearLabel,
}: {
  children: ReactNode;
  onClear?: () => void;
  clearLabel?: string;
}) {
  return (
    <span className="inline-flex h-[34px] items-center gap-[7px] rounded-pill bg-canvas-tint pl-[14px] pr-3 text-chip font-medium">
      {children}
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label={clearLabel}
          className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-muted transition-colors duration-base ease-emphasis hover:bg-[var(--state-press)] hover:text-ink"
        >
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden>
            <path
              d="M1.5 1.5L7.5 7.5M7.5 1.5L1.5 7.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </span>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

/** The List / Board view switcher. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex h-[34px] items-center gap-[3px] rounded-pill bg-canvas-tint p-[3px]"
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex h-7 items-center rounded-pill px-[14px] text-chip',
              'transition-colors duration-base ease-emphasis',
              selected
                ? 'bg-ink font-semibold text-on-ink'
                : 'font-medium text-muted hover:text-ink',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export interface TabItem<T extends string> {
  value: T;
  label: string;
  /** Shown as a small pill after the label, e.g. an activity count. */
  count?: number;
}

/** Underlined tabs, for switching what a detail panel is showing. */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="flex items-center gap-[22px] border-b border-line">
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(item.value)}
            className={cn(
              '-mb-px flex items-center gap-1.5 pb-2.5 text-body transition-colors duration-base ease-emphasis',
              selected
                ? 'border-b-2 border-ink font-semibold text-ink'
                : 'border-b-2 border-transparent text-muted hover:text-ink',
            )}
          >
            {item.label}
            {item.count !== undefined && (
              <span className="inline-flex h-[17px] items-center rounded-pill bg-canvas-tint px-1.5 text-[10.5px] font-semibold text-muted">
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
